import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { MailboxInfo, MyInbox } from '@shared/gmail';
import { errorMessage } from '@/lib/api';
import { formatAgo, formatDate, formatNumber, formatTime, daysFromToday, plural } from '@/lib/format';
import { useMe, useTeam } from '@/lib/hooks';
import { closeDialog, openAdmin } from '@/lib/overlays';
import { openAccount } from '@/shell/AccountDialog';
import { Callout, Chip, Empty, Skeleton, type ChipTone } from '@/ui/bits';
import { GoogleMark, Icon } from '@/ui/Icon';
import { disconnectMine } from './Inboxes';
import { LogRows } from './log';
import { connectUrl, myInboxQuery, myLogPage } from './queries';

// "Gmail and calendar" in your account settings: your inbox's connection, the guard's promise and your own
// access log.

function statusChip(inbox: MyInbox): {
  label: string;
  tone: ChipTone;
  icon?: 'check' | 'warn' | 'x-circle';
} | null {
  const mb = inbox.mailbox;
  if (!mb || mb.status === 'disconnected')
    return inbox.configured ? { label: 'Not connected', tone: 'warning', icon: 'warn' } : null;
  if (!mb.live) return { label: 'Sample', tone: 'plain' };
  if (mb.status === 'error') return { label: 'Needs reconnecting', tone: 'danger', icon: 'x-circle' };
  return { label: 'Connected', tone: 'success', icon: 'check' };
}

/** "today 06:00", "yesterday 18:12" or "12 Oct". */
function whenShort(at: string): string {
  const d = daysFromToday(at);
  if (d === 0) return `today ${formatTime(at)}`;
  if (d === -1) return `yesterday ${formatTime(at)}`;
  return formatDate(at);
}

/** The second line under your address. */
function statusLine(mb: MailboxInfo, push: boolean): string {
  if (!mb.live) return 'Sample inbox · its mail is sample data';
  if (mb.status === 'error') return 'Google stopped accepting Clientbook’s access';
  const parts: string[] = [];
  if (push && mb.watchRenewedAt) parts.push(`push renewed ${whenShort(mb.watchRenewedAt)}`);
  else if (mb.lastSyncAt) parts.push(`checked ${formatAgo(mb.lastSyncAt).toLowerCase()}`);
  else parts.push('first check under way');
  if (mb.connectedAt) parts.unshift(`Connected ${formatDate(mb.connectedAt)}`);
  return parts.join(' · ');
}

export function AccountInbox() {
  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery(myInboxQuery);

  return (
    <section className="dlg-sec gm-acct" aria-labelledby="acct-inbox-h" aria-busy={isLoading || undefined}>
      <div className="acct-sec-h">
        <h3 className="flabel" id="acct-inbox-h">
          Gmail and calendar
        </h3>
        {data ? <StatusChip inbox={data} /> : null}
      </div>
      {isLoading ? (
        <Skeleton rows={2} />
      ) : isError || !data ? (
        <Callout
          tone="danger"
          title="Your Gmail connection did not load"
          role="alert"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => refetch()} disabled={isRefetching}>
              <Icon name="refresh" />
              {isRefetching ? 'Trying again' : 'Try again'}
            </button>
          }
        >
          {errorMessage(error)}
        </Callout>
      ) : (
        <InboxBody inbox={data} />
      )}
    </section>
  );
}

function StatusChip({ inbox }: { inbox: MyInbox }) {
  const chip = statusChip(inbox);
  if (!chip) return null;
  return (
    <Chip tone={chip.tone} icon={chip.icon}>
      {chip.label}
    </Chip>
  );
}

function NotConfigured() {
  const me = useMe();
  const { active } = useTeam();
  if (me.user.role === 'super_admin') {
    return (
      <Callout
        tone="info"
        icon="mail"
        title="Set up Google to connect Gmail"
        actions={
          <button
            type="button"
            className="btn btn-sm btn-tile"
            onClick={() => {
              closeDialog();
              openAdmin('email');
            }}
          >
            <Icon name="arrow-right" />
            Open Email &amp; inboxes
          </button>
        }
      >
        Add the workspace’s Google OAuth client in the admin console. Then everyone on the team can connect their Gmail
        and calendar here.
      </Callout>
    );
  }
  const admin = active.find((m) => m.role === 'super_admin');
  return (
    <Callout tone="info" icon="mail" title="Gmail connects once Google is set up">
      {admin
        ? `${admin.name} sets up Google for ${me.workspace.name} in the admin console.`
        : `Your super admin sets up Google for ${me.workspace.name} in the admin console.`}{' '}
      Then you can connect your Gmail and calendar here.
    </Callout>
  );
}

function InboxBody({ inbox }: { inbox: MyInbox }) {
  const mb = inbox.mailbox;
  const connected = !!mb && mb.status !== 'disconnected';
  // The question takes the account dialog's place, so the dialog comes back afterwards whatever the answer.
  const disconnect = (mine: MailboxInfo) => void disconnectMine(mine, inbox.configured).then(() => openAccount());

  return (
    <>
      {mb && connected && mb.status === 'error' ? (
        <Callout
          tone="danger"
          className="acct-callout"
          title={`Google stopped accepting Clientbook’s access to ${mb.address}`}
          actions={
            inbox.configured ? (
              <a className="btn btn-sm btn-tile" href={connectUrl('personal')}>
                <Icon name="refresh" />
                Reconnect
              </a>
            ) : null
          }
        >
          {mb.statusDetail ?? 'Access was withdrawn in your Google account, or the password changed.'} Replies to your
          CRM emails reach records again after you reconnect.
        </Callout>
      ) : null}

      {mb && connected ? (
        <div className="dest">
          <span className="svc ggl" aria-hidden="true">
            <GoogleMark />
          </span>
          <div className="grow">
            <b>{mb.address}</b>
            <span>{statusLine(mb, inbox.push)}</span>
          </div>
          {!mb.live && inbox.configured ? (
            <a className="btn btn-sm btn-primary" href={connectUrl('personal')}>
              <Icon name="link" />
              Connect your Gmail
            </a>
          ) : null}
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => disconnect(mb)}>
            <Icon name="logout" />
            Disconnect
          </button>
        </div>
      ) : inbox.configured ? (
        <div className="dest">
          <span className="svc ggl" aria-hidden="true">
            <GoogleMark />
          </span>
          <div className="grow">
            <b>{mb ? mb.address : 'Gmail'}</b>
            <span>
              {mb?.disconnectedAt
                ? `Disconnected ${daysFromToday(mb.disconnectedAt) === 0 ? 'today' : formatDate(mb.disconnectedAt)} · reconnect any time`
                : 'Replies to your CRM emails reach the right records, and meetings come in from your calendar.'}
            </span>
          </div>
          <a className="btn btn-sm btn-primary" href={connectUrl('personal')}>
            <Icon name={mb ? 'refresh' : 'link'} />
            {mb ? 'Reconnect' : 'Connect Gmail'}
          </a>
        </div>
      ) : null}

      {!inbox.configured ? <NotConfigured /> : null}

      {inbox.configured || (mb && connected) ? <GuardNote inbox={inbox} /> : null}

      {inbox.configured || inbox.recent.length > 0 ? <OwnLog inbox={inbox} /> : null}
    </>
  );
}

function GuardNote({ inbox }: { inbox: MyInbox }) {
  const mb = inbox.mailbox;
  const live = !!mb && mb.live && mb.status === 'connected';
  const days = plural(inbox.replyWindowDays, 'day');
  return (
    <>
      <p className="field-help acct-help">
        <Icon name="shield" className="i-xs" />
        <span>
          {live ? (
            <>
              Clientbook opens only replies to CRM conversations from the last <b>{days}</b>, now{' '}
              <b>{plural(inbox.guardedThreads, 'conversation')}</b>. Other mail stays unread, and every access is logged
              below.
            </>
          ) : (
            <>
              Clientbook opens only replies to CRM conversations from the last <b>{days}</b>. Other mail stays unread,
              and every access is logged below.
            </>
          )}
        </span>
      </p>
      {live ? (
        <p className="field-help acct-help">
          <Icon name="calendar" className="i-xs" />
          <span>
            {mb.calendar ? (
              <>
                Calendar connected
                {inbox.upcomingMeetings > 0 ? (
                  <>
                    {' · '}
                    <b>{plural(inbox.upcomingMeetings, 'upcoming meeting')}</b> with people in the CRM
                  </>
                ) : null}
                {mb.calendarSyncedAt ? ` · synced ${formatAgo(mb.calendarSyncedAt).toLowerCase()}` : null}
              </>
            ) : (
              <>
                Meetings come in once you allow calendar access.{' '}
                <a className="link" href={connectUrl('personal')}>
                  Reconnect with calendar access
                </a>
              </>
            )}
          </span>
        </p>
      ) : null}
    </>
  );
}

function OwnLog({ inbox }: { inbox: MyInbox }) {
  const { byId } = useTeam();
  const [all, setAll] = useState(false);
  const log = useInfiniteQuery({
    queryKey: ['gmail', 'me', 'log'],
    queryFn: ({ pageParam, signal }) => myLogPage(pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    enabled: all,
  });
  const entries = all && log.data ? log.data.pages.flatMap((p) => p.entries) : inbox.recent;
  const more = all ? log.hasNextPage : inbox.recent.length >= 6;

  return (
    <div className="gm-own-log">
      <div className="acct-sec-h gm-sub">
        <h4 className="flabel" id="gm-own-log-h">
          Your access log
        </h4>
      </div>
      {all && log.isLoading ? (
        <Skeleton rows={3} />
      ) : all && log.isError ? (
        <p className="field-err" role="alert">
          <Icon name="warn" className="i-xs" />
          {errorMessage(log.error)}{' '}
          <button type="button" className="link" onClick={() => log.refetch()}>
            Try again
          </button>
        </p>
      ) : entries.length === 0 ? (
        <Empty compact icon="eye" title="Nothing logged yet">
          Each time Clientbook checks or opens your inbox, it shows here.
        </Empty>
      ) : (
        <div className="gm-log" aria-live="polite">
          <LogRows entries={entries} byId={byId} own />
        </div>
      )}
      {more && !(all && (log.isLoading || log.isError)) ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost gm-more"
          disabled={log.isFetchingNextPage}
          onClick={() => (all ? log.fetchNextPage() : setAll(true))}
        >
          {log.isFetchingNextPage ? 'Loading' : all ? 'Show older' : 'See all'}
        </button>
      ) : null}
      {all && !log.hasNextPage && log.data && entries.length > 6 ? (
        <p className="hint gm-end">{formatNumber(entries.length)} entries, back to when you first connected.</p>
      ) : null}
    </div>
  );
}
